import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { UserPlus, Download, MessageSquare, Bell, Check, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { useAuth } from "@/contexts/AuthContext";

const SPANISH_MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

export default function Referidos() {
  const { isAdmin, isReadOnly, isOpDetalle } = useAuth();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [points, setPoints] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [queue, setQueue] = useState({ items: [], month: null, year: null });
  const [filter, setFilter] = useState("todos");
  const [openRef, setOpenRef] = useState(false);
  const [refForm, setRefForm] = useState({ name: "", phone: "", note: "" });
  const [msgRef, setMsgRef] = useState(null);
  const [redeemRef, setRedeemRef] = useState(null);
  const [redeemForm, setRedeemForm] = useState({ status: "canjeado", redemption_type: "efectivo", redeemed_amount: 0, payment_method: "Pin Peso BHD", memo: "" });
  const [generating, setGenerating] = useState(false);
  const canCreateReferrers = isAdmin || isOpDetalle;
  const canExport = isAdmin || isReadOnly;

  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;

  const load = useCallback(async () => {
    const queueRequest = isAdmin || isReadOnly
      ? api.get("/referrals/queue", { params: { month: prevMonth, year: prevYear } })
      : Promise.resolve({ data: { items: [], month: prevMonth, year: prevYear } });
    const [p, r, red, q] = await Promise.all([
      api.get("/referrals/points", { params: { month, year } }),
      api.get("/referrers"),
      api.get("/referrals/redemptions", { params: { month, year } }),
      queueRequest,
    ]);
    setPoints(Array.isArray(p.data) ? p.data : []);
    setReferrers(Array.isArray(r.data) ? r.data : []);
    setRedemptions(Array.isArray(red.data) ? red.data : []);
    setQueue(Array.isArray(q.data) ? { items: q.data, month: prevMonth, year: prevYear } : { items: q.data?.items || [], month: q.data?.month ?? prevMonth, year: q.data?.year ?? prevYear });
  }, [isAdmin, isReadOnly, month, prevMonth, prevYear, year]);
  useEffect(() => { load(); }, [load]);

  const filteredPoints = useMemo(() => {
    if (filter === "qualified") return points.filter((p) => p.points >= 500);
    if (filter === "not_qualified") return points.filter((p) => p.points < 500);
    return points;
  }, [points, filter]);

  const chartData = filteredPoints.slice(0, 15).map((p) => ({ name: (p.referrer_name || "Referidor").slice(0, 14), points: p.points || 0, qualified: (p.points || 0) >= 500 }));
  const queueItems = Array.isArray(queue?.items) ? queue.items : [];

  const createReferrer = async () => {
    try { await api.post("/referrers", refForm); toast.success("Referidor creado"); setOpenRef(false); setRefForm({ name: "", phone: "", note: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const generateMessage = (p) => {
    const deadline = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString("es-DO");
    return `Buenas tardes, su total acumulado de MinenePuntos es de ${Math.floor(p.points)} MinenePuntos.

Puede canjearlos de una de estas formas:

1. RD$${fmt.dop(p.crypto_equivalent_dop)} en la criptomoneda que desee.
2. RD$${fmt.dop(p.cash_equivalent_dop)} en efectivo.

Por favor realizar su canje antes del ${deadline}.

Gracias por recomendarnos con sus amigos y familiares.`;
  };

  const notify = async (p) => {
    try { await api.post(`/referrals/notify/${p.referrer_id}`, { month, year }); toast.success("Marcado como notificado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doRedeem = async () => {
    try { await api.post(`/referrals/redeem/${redeemRef.referrer_id}`, redeemForm); toast.success("Canje registrado"); setRedeemRef(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API}/reports/referrals?month=${month}&year=${year}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `referidos_${year}_${month}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const generateQueue = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/referrals/generate-monthly", { month: prevMonth, year: prevYear });
      toast.success(`Cola generada: ${data.generated} referidores con ≥500 pts`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Mensaje copiado al portapapeles");
  };

  const markNotified = async (referrerId) => {
    try { await api.post(`/referrals/notify/${referrerId}`, { month: prevMonth, year: prevYear }); toast.success("Marcado como notificado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const STATUS_BADGE = {
    no_aplica: "bg-slate-200 text-slate-600",
    disponible: "bg-emerald-100 text-emerald-700",
    notificado: "bg-amber-100 text-amber-700",
    canjeado: "bg-sky-100 text-sky-700",
    vencido: "bg-rose-100 text-rose-700",
  };

  return (
    <div className="space-y-6" data-testid="referidos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Referidos · MinenePuntos</h1>
          <p className="text-slate-500 mt-1">1 USDT vendido = 1 MinenePunto. Mínimo de canje: 500 puntos.</p>
        </div>
        <div className="flex gap-2">
          {canExport && <Button variant="outline" onClick={exportCsv} data-testid="export-referrals"><Download size={16} className="mr-2" /> Exportar</Button>}
          {canCreateReferrers && <Button onClick={() => setOpenRef(true)} className="bg-slate-900" data-testid="new-referrer-btn"><UserPlus size={16} className="mr-2" /> Nuevo Referidor</Button>}
        </div>
      </div>

      <Card className="p-4 border border-slate-200 rounded-xl bg-white">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Mes</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="ref-month"><SelectValue /></SelectTrigger>
              <SelectContent>{Array.from({ length: 12 }).map((_, i) => (<SelectItem key={i+1} value={String(i+1)}>{i+1}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Año</Label>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || today.getFullYear())} className="w-28" data-testid="ref-year" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Filtro</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-56" data-testid="ref-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="qualified">500+ puntos (canje)</SelectItem>
                <SelectItem value="not_qualified">Menos de 500</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 rounded-xl bg-white">
        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4">MinenePuntos por referidor</h3>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} angle={-30} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip />
              <Bar dataKey="points" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.qualified ? "#10b981" : "#94a3b8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-4 mt-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /> 500+ pts (canje disponible)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-400 rounded-sm" /> &lt; 500 pts</span>
        </div>
      </Card>

      <Tabs defaultValue={isAdmin || isReadOnly ? "queue" : "points"}>
        <TabsList className="bg-white border border-slate-200">
          {(isAdmin || isReadOnly) && <TabsTrigger value="queue" data-testid="tab-queue">Cola Mensual</TabsTrigger>}
          <TabsTrigger value="points" data-testid="tab-points">Puntos del mes</TabsTrigger>
          <TabsTrigger value="referrers" data-testid="tab-referrers">Referidores</TabsTrigger>
          {(isAdmin || isReadOnly) && <TabsTrigger value="redemptions" data-testid="tab-redemptions">Canjes</TabsTrigger>}
        </TabsList>

        {(isAdmin || isReadOnly) && <TabsContent value="queue" className="mt-4 space-y-4">
          <Card className="p-5 border border-slate-200 rounded-xl bg-white">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
                  Cola de mensajes — {SPANISH_MONTHS[prevMonth - 1]} {prevYear}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Se genera automáticamente el día 1 de cada mes a las 9:00 AM AST. Los puntos cortan por mes calendario y no se acumulan.
                </p>
              </div>
              {isAdmin && <Button onClick={generateQueue} disabled={generating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-queue">
                <Sparkles size={16} className="mr-2" />
                {generating ? "Generando..." : "Generar / Regenerar cola"}
              </Button>}
            </div>
          </Card>

          <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="queue-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Referidor</th>
                  <th className="px-4 py-3 text-left font-semibold">Teléfono</th>
                  <th className="px-4 py-3 text-right font-semibold">Puntos</th>
                  <th className="px-4 py-3 text-right font-semibold">Equiv DOP cripto / efectivo</th>
                  <th className="px-4 py-3 text-left font-semibold">Estado</th>
                  <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {queueItems.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-400">
                    Sin cola para {SPANISH_MONTHS[prevMonth - 1]} {prevYear}. Pulsa "Generar cola" para crear los mensajes.
                  </td></tr>
                )}
                {queueItems.map((q) => (
                  <tr key={q.id || q.referrer_id} className="border-t border-slate-100" data-testid={`queue-row-${q.referrer_id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{q.referrer_name}</td>
                    <td className="px-4 py-3 font-mono tabular-nums text-slate-600">{q.referrer_phone || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-bold text-emerald-600">{Math.floor(q.points)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-xs text-slate-600">
                      RD${fmt.dop(q.crypto_equivalent_dop)} / RD${fmt.dop(q.cash_equivalent_dop)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={STATUS_BADGE[q.status] || "bg-slate-200"}>{q.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setMsgRef({ ...q, _generated: true })} title="Ver mensaje" data-testid={`view-msg-${q.referrer_id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600">
                          <MessageSquare size={14} />
                        </button>
                        <button onClick={() => copyMessage(q.generated_message)} title="Copiar mensaje" data-testid={`copy-msg-${q.referrer_id}`} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600">
                          <Copy size={14} />
                        </button>
                        {isAdmin && q.status === "disponible" && (
                          <button onClick={() => markNotified(q.referrer_id)} title="Marcar enviado/notificado" data-testid={`mark-notif-${q.referrer_id}`} className="p-1.5 rounded hover:bg-amber-100 text-amber-600">
                            <Bell size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>}

        <TabsContent value="points" className="mt-4">
          <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="points-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Referidor</th>
                    <th className="px-4 py-3 text-right font-semibold">Clientes</th>
                    <th className="px-4 py-3 text-right font-semibold">MinenePuntos</th>
                    <th className="px-4 py-3 text-right font-semibold">Equiv Cripto</th>
                    <th className="px-4 py-3 text-right font-semibold">Equiv Efectivo</th>
                    <th className="px-4 py-3 text-left font-semibold">Estado</th>
                    <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="font-mono tabular-nums">
                  {filteredPoints.length === 0 && (<tr><td colSpan={7} className="text-center py-8 text-slate-400">Sin puntos en este mes</td></tr>)}
                  {filteredPoints.map((p) => {
                    const qualified = p.points >= 500;
                    return (
                      <tr key={p.referrer_id} className="border-t border-slate-100">
                        <td className="px-4 py-3 font-sans font-semibold text-slate-800">{p.referrer_name}</td>
                        <td className="px-4 py-3 text-right">{p.clients_count}</td>
                        <td className={`px-4 py-3 text-right font-bold ${qualified ? "text-emerald-600" : "text-slate-500"}`}>{fmt.usdt(p.points)}</td>
                        <td className="px-4 py-3 text-right">RD${fmt.dop(p.crypto_equivalent_dop)}</td>
                        <td className="px-4 py-3 text-right">RD${fmt.dop(p.cash_equivalent_dop)}</td>
                        <td className="px-4 py-3"><Badge className={STATUS_BADGE[p.status] || "bg-slate-200"}>{p.status}</Badge></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <button title="Generar mensaje" disabled={!qualified} onClick={() => setMsgRef(p)} data-testid={`msg-${p.referrer_id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"><MessageSquare size={14} /></button>
                            {isAdmin && <button title="Marcar notificado" disabled={!qualified} onClick={() => notify(p)} data-testid={`notify-${p.referrer_id}`} className="p-1.5 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed"><Bell size={14} /></button>}
                            {isAdmin && <button title="Marcar canjeado" disabled={!qualified} onClick={() => { setRedeemRef(p); setRedeemForm({ status: "canjeado", redemption_type: "efectivo", redeemed_amount: p.cash_equivalent_dop, payment_method: "Pin Peso BHD", memo: "" }); }} data-testid={`redeem-${p.referrer_id}`} className="p-1.5 rounded hover:bg-sky-100 text-sky-600 disabled:opacity-30 disabled:cursor-not-allowed"><Check size={14} /></button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="referrers" className="mt-4">
          <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="referrers-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Nombre</th>
                  <th className="px-4 py-3 text-left font-semibold">Teléfono</th>
                  <th className="px-4 py-3 text-left font-semibold">Nota</th>
                  <th className="px-4 py-3 text-left font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {referrers.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-slate-800">{r.name}</td>
                    <td className="px-4 py-3 text-slate-600">{r.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.note || "—"}</td>
                    <td className="px-4 py-3"><Badge className={r.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200"}>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {(isAdmin || isReadOnly) && <TabsContent value="redemptions" className="mt-4">
          <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="redemptions-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Referidor</th>
                  <th className="px-4 py-3 text-left font-semibold">Mes</th>
                  <th className="px-4 py-3 text-right font-semibold">Puntos</th>
                  <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                  <th className="px-4 py-3 text-right font-semibold">Monto</th>
                  <th className="px-4 py-3 text-left font-semibold">Método</th>
                  <th className="px-4 py-3 text-left font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody className="font-mono tabular-nums">
                {redemptions.length === 0 && (<tr><td colSpan={7} className="text-center py-8 text-slate-400">Sin canjes</td></tr>)}
                {redemptions.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-sans">{r.referrer_name}</td>
                    <td className="px-4 py-3">{r.year}-{String(r.month).padStart(2,"0")}</td>
                    <td className="px-4 py-3 text-right">{fmt.usdt(r.points)}</td>
                    <td className="px-4 py-3 font-sans">{r.redemption_type || "—"}</td>
                    <td className="px-4 py-3 text-right">{r.redeemed_amount ? fmt.dop(r.redeemed_amount) : "—"}</td>
                    <td className="px-4 py-3 font-sans">{r.payment_method || "—"}</td>
                    <td className="px-4 py-3"><Badge className={STATUS_BADGE[r.status]}>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>}
      </Tabs>

      {/* New referrer */}
      <Dialog open={openRef} onOpenChange={setOpenRef}>
        <DialogContent data-testid="new-referrer-dialog">
          <DialogHeader><DialogTitle>Nuevo referidor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label><Input value={refForm.name} onChange={(e) => setRefForm({ ...refForm, name: e.target.value })} data-testid="ref-name" /></div>
            <div><Label>Teléfono</Label><Input value={refForm.phone} onChange={(e) => setRefForm({ ...refForm, phone: e.target.value })} /></div>
            <div><Label>Nota</Label><Textarea value={refForm.note} onChange={(e) => setRefForm({ ...refForm, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRef(false)}>Cancelar</Button>
            <Button onClick={createReferrer} className="bg-slate-900" data-testid="save-referrer">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message generator */}
      <Dialog open={!!msgRef} onOpenChange={(o) => !o && setMsgRef(null)}>
        <DialogContent data-testid="message-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Mensaje de canje para {msgRef?.referrer_name}</DialogTitle></DialogHeader>
          {msgRef && (
            <>
              <Textarea readOnly value={msgRef._generated && msgRef.generated_message ? msgRef.generated_message : generateMessage(msgRef)} className="font-mono text-xs h-64" data-testid="message-text" />
              {msgRef.referrer_phone && (
                <p className="text-xs text-slate-500">Teléfono guardado: <span className="font-mono">{msgRef.referrer_phone}</span></p>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setMsgRef(null)}>Cerrar</Button>
                <Button onClick={() => { navigator.clipboard.writeText(msgRef._generated && msgRef.generated_message ? msgRef.generated_message : generateMessage(msgRef)); toast.success("Copiado"); }} className="bg-slate-900" data-testid="copy-message">Copiar al portapapeles</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Redeem dialog */}
      <Dialog open={!!redeemRef} onOpenChange={(o) => !o && setRedeemRef(null)}>
        <DialogContent data-testid="redeem-dialog">
          <DialogHeader><DialogTitle>Registrar canje · {redeemRef?.referrer_name}</DialogTitle></DialogHeader>
          {redeemRef && (
            <div className="space-y-3">
              <div>
                <Label>Forma de canje</Label>
                <Select value={redeemForm.redemption_type} onValueChange={(v) => setRedeemForm({ ...redeemForm, redemption_type: v, redeemed_amount: v === "efectivo" ? redeemRef.cash_equivalent_dop : redeemRef.crypto_equivalent_dop })}>
                  <SelectTrigger data-testid="redeem-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo (RD$)</SelectItem>
                    <SelectItem value="cripto">Cripto (RD$ en cripto)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Monto canjeado</Label><Input type="number" step="0.01" value={redeemForm.redeemed_amount} onChange={(e) => setRedeemForm({ ...redeemForm, redeemed_amount: parseFloat(e.target.value) })} data-testid="redeem-amount" /></div>
              <div><Label>Método de pago / Moneda</Label><Input value={redeemForm.payment_method} onChange={(e) => setRedeemForm({ ...redeemForm, payment_method: e.target.value })} data-testid="redeem-method" /></div>
              <div><Label>Memo</Label><Textarea value={redeemForm.memo} onChange={(e) => setRedeemForm({ ...redeemForm, memo: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemRef(null)}>Cancelar</Button>
            <Button onClick={doRedeem} className="bg-sky-600" data-testid="confirm-redeem">Registrar canje</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
